/**
 * How to reach the organization: the contact block an admin writes on
 * /admin/about, rendered wherever a page needs to end in "get in touch".
 *
 * Lifted out of About.tsx when the hosting guide became its second reader.
 * The title is a prop because the two pages ask for it differently — About is
 * telling you who we are, Host is asking you to start something — but the rows
 * are identical and must stay that way, so a phone number formats the same on
 * both.
 */
import type { CSSProperties, ReactNode } from "react";
import type { AboutContact } from "@alpha/shared";
import { telHref } from "@alpha/shared";
import { mapsSearchHref } from "../lib/format";
import {
  FacebookIcon,
  InstagramIcon,
  MailIcon,
  MapPinIcon,
  PhoneIcon,
  SectionCard,
  XSocialIcon,
} from "./ui";

/** One line of the card: an icon plate, then whatever it labels. */
function ContactRow({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-50 text-brandText">
        {icon}
      </span>
      <div className="min-w-0 break-words text-slate-600">{children}</div>
    </div>
  );
}

/**
 * `noreferrer noopener` on every outbound link: these destinations are typed by
 * an admin, and a new tab should learn nothing about where it came from.
 */
function OutboundLink({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a
      href={href}
      target="_blank"
      rel="noreferrer noopener"
      className="font-medium text-brandText hover:underline"
    >
      {children}
    </a>
  );
}

/**
 * Is there anything to show? Exported so a page can decide whether to make room
 * for the card at all — a heading over an empty card is worse than no card.
 */
export function hasContact(contact: AboutContact): boolean {
  return Object.values(contact).some((v) => typeof v === "string" && v.trim() !== "");
}

export function ContactCard({
  contact,
  title = "Get in touch",
  style,
}: {
  contact: AboutContact;
  title?: string;
  style?: CSSProperties;
}) {
  const socials = [
    { url: contact.facebookUrl, label: "Facebook", Icon: FacebookIcon },
    { url: contact.instagramUrl, label: "Instagram", Icon: InstagramIcon },
    { url: contact.xUrl, label: "X", Icon: XSocialIcon },
  ].filter((s): s is { url: string; label: string; Icon: typeof FacebookIcon } => !!s.url);

  return (
    <SectionCard title={title} className="animate-rise" style={style}>
      <div className="space-y-3">
        {contact.email ? (
          <ContactRow icon={<MailIcon className="h-4 w-4" />}>
            <a
              href={`mailto:${contact.email}`}
              className="font-medium text-brandText hover:underline"
            >
              {contact.email}
            </a>
          </ContactRow>
        ) : null}

        {contact.phone ? (
          <ContactRow icon={<PhoneIcon className="h-4 w-4" />}>
            <a href={telHref(contact.phone)} className="font-medium text-brandText hover:underline">
              {contact.phone}
            </a>
          </ContactRow>
        ) : null}

        {contact.address ? (
          <ContactRow icon={<MapPinIcon className="h-4 w-4" />}>
            <OutboundLink href={contact.mapsUrl ?? mapsSearchHref(contact.address)}>
              {contact.address}
            </OutboundLink>
          </ContactRow>
        ) : null}

        {/* The platform's name sits beside its mark, so recognition never rests
            on the simplified glyph. */}
        {socials.map(({ url, label, Icon }) => (
          <ContactRow key={label} icon={<Icon className="h-4 w-4" />}>
            <OutboundLink href={url}>{label}</OutboundLink>
          </ContactRow>
        ))}
      </div>
    </SectionCard>
  );
}
